import { useShare } from '@/store/share'
import { CrossOriginCommunity } from '@pikpak/cross-origin-community'
import { useEventListener } from '@vueuse/core'
import { ElLoading } from 'element-plus'
import qs from 'qs'
import RecordSelected from '~/utils/RecordSelected'
import { useUserInfo } from '~~/store/userInfo'
import { isImage, isVideo } from '~~/utils'

export const RESTORE_TELEPORT_ID = 'restore_teleport'
export const RESTORE_TELEPORT_MOBILE_ID = 'restore_teleport_mobile'

// webDecompression跨源通信key
export const enum DecompressionCrossCommunityKey {
  DECOMPRESSION_CLOSE = 'decompression-close',
  ROUTING_JUMP = 'routing-jump',
  REFRESH_USER_INFO = 'refresh-user-info',
  /**
   * 用于iframe内进行三方登录（过滤方案，解决账号sdk无法再iframe内发起google oAuth的bug)
   * 事件由@pikpak/xlco-login-ui-component发出
   */
  PP_IFRAME_REQUEST_O_AUTH = 'pp-iframe-request-o-auth',
  // 用于三方登录后还原解压状态的一些信息
  AFTER_OAUTH_RECOVER_PARAMS = 'AFTER_OAUTH_RECOVER_PARAMS',
  RECOVER_PARAMS = 'RECOVER_PARAMS',
}

// 用于页面三方登录跳走后
let currentIframeLink = ''
let recoverStatusParams: Record<string, unknown> = {}
const STORE_RECOVER_STATUS_KEY = 'recover-status-params'
const storeRecoverStatusParams = () => {
  try {
    window.localStorage.setItem(
      STORE_RECOVER_STATUS_KEY,
      JSON.stringify({
        recoverStatusParams,
        timestamp: Date.now(),
        iframeLink: currentIframeLink,
      }),
    )
  } catch (err) {
    console.error('store recover status params error: ', err)
  }
}
const getRecoverStatusParams = () => {
  try {
    const result = window.localStorage.getItem(STORE_RECOVER_STATUS_KEY)
    if (!result) {
      return {}
    }
    const params = JSON.parse(result)
    // 如果存储的信息超过2分钟即为过期，不再使用
    if (params.timestamp && Date.now() - params.timestamp > 2 * 60 * 1000) {
      return {}
    }
    return params
  } catch (err) {
    console.error('get recover status params error: ', err)
    return {}
  } finally {
    window.localStorage.removeItem(STORE_RECOVER_STATUS_KEY)
  }
}
const removeRecoverStatusParams = () => {
  window.localStorage.removeItem(STORE_RECOVER_STATUS_KEY)
}

// 使用iframe打开web在线解压窗口
export const useWebDecompression = () => {
  if (import.meta.server) {
    return { show() {} }
  }

  const iframe: HTMLIFrameElement = document.createElement('iframe')
  const comm = new CrossOriginCommunity(iframe.contentWindow!, '*')
  iframe.style.cssText = `
    display: none;
    position: fixed;
    bottom: 0;
    width: 100vw;
    height: var(--vh, 100vh);
    border: none;
    z-index: 2000;` // z-index 为 Dialog 级别

  const hideIframe = () => {
    iframe.style.display = 'none'
    iframe.src = ''
    window.scenePreviewMedia = false
  }

  const showIframe = (link: string) => {
    currentIframeLink = link
    if (link === iframe.src) {
      return
    }

    const loading = ElLoading.service({
      lock: true,
      background: 'rgba(0, 0, 0, 0.5)',
    })

    useEventListener(
      window,
      'keyup',
      e => {
        if (e.key === 'Esc' && loading.visible) {
          loading.close()
          hideIframe()
        }
      },
      { once: true },
    )

    iframe.addEventListener(
      'load',
      () => {
        const newComm = new CrossOriginCommunity(iframe.contentWindow!, '*')
        newComm.postMessage({ type: DecompressionCrossCommunityKey.RECOVER_PARAMS, data: getRecoverStatusParams() })
        loading.close()
      },
      { once: true },
    )

    iframe.src = link
    iframe.style.display = 'block'
    window.scenePreviewMedia = true
  }

  // 是oAuth登录回来的请求，自动还原数据
  const recoverParams = getRecoverStatusParams()
  if (recoverParams?.recoverStatusParams && recoverParams?.iframeLink) {
    const actionParams = JSON.stringify(recoverParams.recoverStatusParams)
    showIframe(`${recoverParams.iframeLink}&action=recover&actionParams=${encodeURIComponent(actionParams)}`)
  }

  window.document.body.appendChild(iframe)

  const userInfo = useUserInfo()
  comm.addListener(({ type, data }) => {
    try {
      if (type === DecompressionCrossCommunityKey.DECOMPRESSION_CLOSE) {
        removeRecoverStatusParams()
        iframe.style.display = 'none'
        window.scenePreviewMedia = false
      }

      if (type === DecompressionCrossCommunityKey.ROUTING_JUMP) {
        window.location.href = (data as Record<string, string>)?.url || '/drive'
      }

      if (type === DecompressionCrossCommunityKey.REFRESH_USER_INFO) {
        userInfo.refresh(true)
      }

      if (type === DecompressionCrossCommunityKey.AFTER_OAUTH_RECOVER_PARAMS) {
        recoverStatusParams = data as Record<string, string>
      }

      if (type === DecompressionCrossCommunityKey.PP_IFRAME_REQUEST_O_AUTH) {
        const { oAuthLink } = (data as Record<string, string>) || {}
        if (oAuthLink) {
          storeRecoverStatusParams()
          window.location.href = oAuthLink.replace(/origin=([^&]+)/i, (o: string, dist: string) =>
            dist ? `origin=${encodeURIComponent(window.location.href)}` : dist,
          )
        }
      }
      // 支付相关
      if (type === 'payment-close') {
        const shareStore = useShare()
        if ((data as { type: string }).type === 'success') {
          console.info('premium success')
          shareStore.updatePremiumCallback?.()
        }
        shareStore.fileSpaceNotEnoughVisible = false
        shareStore.updatePremiumCallback = undefined
        hideIframe()
      }
    } catch (err) {
      console.error('cross origin community error: ', err)
    }
  })

  return {
    show: showIframe,
    hide: hideIframe,
  }
}

export const isVideoOrImage = (extension: string, mimeType: string) =>
  isVideo(extension, mimeType) || isImage(extension, mimeType)

export const createTorrentTask = (url: string) => {
  // http://dev.mypikpak.com/drive/all?action=create_task&url=magnet%253A%253Fxt%253Durn%253Abtih%253A74cfb51704f0c703c08111189f1f367dbb6dfba1&launcher=homepage
  const params = qs.stringify({
    action: 'create_task',
    url,
    launcher: 'share',
  })
  window.open(`${window.location.origin}/drive/all?${params}`, '_blank')
}

/** 通用判断遮罩元素来识别当前是否有弹窗 */
export const hasMaskElm = (elms: HTMLElement[]) =>
  elms.some(el => {
    const computedStyle = window.getComputedStyle(el)
    const { position, top, right, bottom, left } = computedStyle
    const { width, height } = el.getBoundingClientRect() // 如果元素不可见，宽高为 0
    return width && height && position === 'fixed' && [top, right, bottom, left].every(size => size === '0px')
  })

export function hasAttrInEventPath(e: Event): HTMLElement[]
export function hasAttrInEventPath(e: Event, attr: string): boolean
export function hasAttrInEventPath(e: Event, attr?: string): HTMLElement[] | boolean {
  const elements = e.composedPath().filter((el): el is HTMLElement => el instanceof HTMLElement)

  if (typeof attr === 'undefined') return elements

  return elements.some(el => {
    const attrVal = el.attributes.getNamedItem(attr)?.value
    return attrVal !== 'false' && attrVal !== undefined
  })
}

export const recordSelected = new RecordSelected()
